/**
 * useMessagingTransport — Transport Abstraction Hook
 * ====================================================
 *
 * Switches between WebSocket and SSE based on REACT_APP_WS_ENABLED.
 *
 * When WS enabled:
 *  - Connects websocketService with the user's JWT token
 *  - Maps WS events → handler callbacks (same shape as SSE handlers)
 *  - Provides join/leave/sendMessage/sendTyping via WS
 *
 * When WS disabled:
 *  - Delegates to existing useMessagingSSE hook
 *  - join/leave/sendTyping are no-ops
 *  - sendMessage is null (caller should use REST MessagingAPI)
 *
 * @param {string} userId  Current user ID
 * @param {object} handlers  { onMessageCreated, onMessageRead, onThreadEscalated, onGuardFlagged, onTyping, onQuotaExceeded }
 * @returns {{ isConnected, transport, joinThread, leaveThread, sendMessage, sendTyping }}
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useMessagingSSE } from './useMessagingSSE';
import wsService from '../services/websocketService';

const WS_ENABLED = process.env.REACT_APP_WS_ENABLED === 'true';

function getToken() {
  return null;
}

export function useMessagingTransport(userId, handlers = {}) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  // ── SSE path (fallback) ────────────────────────────────────────
  const sseResult = useMessagingSSE(
    WS_ENABLED ? null : userId,   // disable SSE when WS is on
    WS_ENABLED ? {} : handlers,
  );

  // ── WS path ────────────────────────────────────────────────────
  const [wsConnected, setWsConnected] = useState(false);

  useEffect(() => {
    if (!WS_ENABLED || !userId) return;

    const token = getToken();
    if (!token) return;

    // Subscribe to events
    const unsubs = [
      wsService.on('connected', () => setWsConnected(true)),
      wsService.onStateChange((s) => setWsConnected(s === 'connected')),

      wsService.on('new_message', (data) => {
        handlersRef.current.onMessageCreated?.({
          thread_id: data?.thread_id,
          message_id: data?.id,
          ...data,
        });
      }),

      wsService.on('joined', () => {}),
      wsService.on('left', () => {}),

      wsService.on('typing', (data) => {
        handlersRef.current.onTyping?.(data);
      }),

      wsService.on('error', (data) => {
        if (data?.code === 'QUOTA_EXCEEDED') {
          handlersRef.current.onQuotaExceeded?.(data);
        }
      }),

      wsService.on('quota_exceeded', (data) => {
        handlersRef.current.onQuotaExceeded?.(data);
      }),
    ];

    wsService.connect(token);

    return () => {
      unsubs.forEach((fn) => fn());
      wsService.disconnect();
    };
  }, [userId]);

  // ── Unified interface ──────────────────────────────────────────
  const joinThread = useCallback((threadId) => {
    if (WS_ENABLED) wsService.join(threadId);
  }, []);

  const leaveThread = useCallback((threadId) => {
    if (WS_ENABLED) wsService.leave(threadId);
  }, []);

  const sendMessage = useCallback((threadId, content, messageType = 'text') => {
    if (WS_ENABLED) {
      wsService.sendMessage(threadId, content, messageType);
      return true;
    }
    return false; // caller should use REST
  }, []);

  const sendTyping = useCallback((threadId) => {
    if (WS_ENABLED) wsService.sendTyping(threadId);
  }, []);

  return {
    isConnected: WS_ENABLED ? wsConnected : sseResult.isConnected,
    transport: WS_ENABLED ? 'ws' : 'sse',
    joinThread,
    leaveThread,
    sendMessage,
    sendTyping,
  };
}
